import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import AccessDenied from "../../../components/access_denied/access_denied";
import MainLayout from "../../../layouts/main/main_layout";
import SchemeLayout from "../../../layouts/schemes/scheme_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import {
  deleteSchemes,
  getSchemes,
  sendNotification,
} from "../../../services/schemes/schemes";
import NotifyUserPopup from "../components/notify_user_popup/notify_user_popup";
import SchemesListing from "../components/schemes_listing.tsx/schemes_listing";
import DeleteScheme from "../delete_scheme/delete_scheme";
import { Scheme } from "../models/schemes";
import FilterSchemes, {
  SchemesFilterValues,
} from "../components/filter_schemes/filter_schemes";
import useScheme, { SchemeProvider } from "../providers/scheme_provider";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import SortSchemesPopup, {
  DEFAULT_SCHEMES_SORT_OPTION,
  SchemesSortOption,
  schemesSortOptionToParams,
} from "../components/sort_schemes_popup/sort_schemes_popup";

export default function Schemes() {
  return (
    <SchemeProvider>
      <SchemesContent />
    </SchemeProvider>
  );
}

function SchemesContent() {
  const { schemeMaster } = useScheme();
  const { selectedOrgId } = useSelectedOrgStore();
  const [deleteScheme, setDeleteScheme] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [viewSchemeDetails, setViewSchemeDetails] = useState(false);
  const [selectedSchemeDetails, setSelectedSchemeDetails] = useState<Scheme>(
    {} as Scheme
  );
  const [searchParams, setUrlParam] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [page, setPage] = useState(
    searchParams?.has("page") ? parseInt(searchParams.get("page")!)  : 1
  );
  const [pageSize, setPageSize] = useState(8);
  const [sortOption, setSortOption] = useState<SchemesSortOption>(DEFAULT_SCHEMES_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<SchemesFilterValues>({
    governmentType: null,
    categoryId: null,
    fromDate: "",
    toDate: "",
  });
  const activeFilterCount = [
    filters.governmentType,
    filters.categoryId,
    filters.fromDate && filters.toDate,
  ].filter(Boolean).length;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, filters, selectedOrgId, sortOption, pageSize]);

  const [notification, setNotification] = useState<{
    [key: string]: {
      title: string;
      description: string;
      enabled: boolean;
      sameAsEnglish: boolean;
    };
  }>({});
  const [sameAsEnglish, setSameAsEnglish] = useState<Object>({
    hi: true,
    te: true,
    ta: true,
    bn: true,
    kn: true,
    ml: true,
    mr: true,
    gu: true,
    ur: true,
    pa: true,
    or: true,
    as: true,
  });
  const [openNotificationPopUp, setOpenNotificationPopup] = useState(false);
  const [notificationSubmitting, setNotificationSubmitting] = useState(false);
  const { showSnackbar } = useFeedback();
  const { hasPermission } = useUser();

  useEffect(() => {
    setUrlParam({ page: (page).toString() });
  }, [page, setUrlParam]);

  const { data: schemes, isLoading, isError } = useQuery(
    ["schemes", debouncedSearchValue, page, pageSize, sortOption, filters, selectedOrgId],
    async () => {
      const { sortBy, sortOrder } = schemesSortOptionToParams(sortOption);
      let payload = {
        page: page,
        size: pageSize,
        keyword: debouncedSearchValue,
        sortBy: sortBy,
        sortOrder: sortOrder,
        governmentType: filters.governmentType || undefined,
        categoryId: filters.categoryId || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        orgId: selectedOrgId || undefined,
      };
      let response = await getSchemes(payload);
      return response.data.data;
    }
  );

  const deleteSchemeApiCall = () => {
    setDeleteSubmitting(true);
    deleteSchemes(selectedSchemeDetails?.schemeId)
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Scheme deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["schemes"] });
          setDeleteScheme(false);
        }
      })
      .catch((e) => {
        if (
          e.response.status === 403
        ) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong while deleting the scheme.");
        }
      })
      .finally(() => setDeleteSubmitting(false));
  };

  function submitNotification() {
    let payload: { title: string; description: string; locale: string }[] = [];

    Object.keys(notification).map((locale) => {
      if (notification[locale].enabled) {
        payload.push({
          title: notification[locale].title,
          description: notification[locale].sameAsEnglish
            ? notification["en"].description
            : notification[locale].description,
          locale: locale,
        });
      }
    });

    setNotificationSubmitting(true);
    sendNotification(payload, selectedSchemeDetails?.schemeId)
      .then((res) => {
        showSnackbar("Notification sent successfully");
        setNotification({});
        setOpenNotificationPopup(false);
      })
      .catch((e) => {
        if (
          e.response.status === 403
        ) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong while sending the notification.");
        }
      })
      .finally(() => setNotificationSubmitting(false));
  }

  return (
    <MainLayout>
      {hasPermission("schemes", "read") ? (
        <SchemeLayout setPage={setPage} page={page}>
          <SchemesListing
            searchList={schemes?.list || []}
            deleteScheme={deleteScheme}
            viewSchemeDetails={viewSchemeDetails}
            selectedSchemeDetails={selectedSchemeDetails}
            setSelectedSchemeDetails={setSelectedSchemeDetails}
            setDeleteScheme={setDeleteScheme}
            setViewSchemeDetails={setViewSchemeDetails}
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            count={schemes?.total || 0}
            isLoading={isLoading}
            isError={isError}
            setNotificationPopUp={setOpenNotificationPopup}
            sortOption={sortOption}
            onOpenSort={() => setSortDialogOpen(true)}
            activeFilterCount={activeFilterCount}
            onOpenFilters={() => setFilterDialogOpen(true)}
          />
          <DeleteScheme
            deleteScheme={deleteScheme}
            setDeleteScheme={setDeleteScheme}
            selectedSchemeDetails={selectedSchemeDetails}
            deleteSchemeApiCall={deleteSchemeApiCall}
            submitting={deleteSubmitting}
          />
          {openNotificationPopUp && (
            <NotifyUserPopup
              schemeId={selectedSchemeDetails.schemeId.toString()}
              openPopup={openNotificationPopUp}
              setOpenPopup={setOpenNotificationPopup}
              notification={notification}
              setNotification={setNotification}
              sameAsEnglish={sameAsEnglish}
              setSameAsEnglish={setSameAsEnglish}
              submit={submitNotification}
              setOpenNotificationPopup={setOpenNotificationPopup}
              submitting={notificationSubmitting}
            />
          )}
          <SortSchemesPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
          {filterDialogOpen && (
            <FilterSchemes
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              governmentList={schemeMaster?.schemeGovernment || []}
              categoryList={schemeMaster?.schemeCategory || []}
              governmentType={filters.governmentType}
              categoryId={filters.categoryId}
              fromDate={filters.fromDate}
              toDate={filters.toDate}
              onApply={setFilters}
            />
          )}
        </SchemeLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
